import {provideHttpClient, withFetch, withInterceptors} from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import {provideRouter, withComponentInputBinding, withInMemoryScrolling} from '@angular/router';
import {Ability} from '@casl/ability';
import {provideTransloco} from '@jsverse/transloco';
import {provideTranslocoPersistLang} from '@jsverse/transloco-persist-lang';
import {provideOptimus} from '@openng/optimus-ui/config';
import {
  ACCESS_MANAGEMENT_CONFIG,
  API_CONFIG,
  API_URL,
  countryInterceptor,
  ENV_NAME,
  PRODUCT_CONFIG_TOKEN,
  retryOnNetworkErrorInterceptor,
} from '@scalified-monorepo/ng-shared';
import {AuthStore, commandMetadataInterceptor, unauthorizedInterceptor} from '@scalified-monorepo/ng-shared/auth';
import {GlobalErrorHandler} from '@scalified-monorepo/ng-shared/errors';
import {provideGlobalExecution} from '@scalified-monorepo/ng-shared/global-execution';
import {LOGGER_CONFIG} from '@scalified-monorepo/ng-shared/logger';
import {RootStore} from '@scalified-monorepo/ng-shared/ngrx';
import {CqrsEventFactory, SseStore} from '@scalified-monorepo/ng-shared/sse';
import {createSampleAppAbility, sampleAccessManagementConfig} from '@scalified-monorepo/sample-shared';
import {Angulartics2Module} from 'angulartics2';
import {environment} from '../environments/environment';
import {routes} from './app.routes';
import {ProductConfigService, SUPPORTED_LANGUAGES} from './core/config/product-config';
import {optimusPreset} from './optimus-preset';
import {CustomTranslocoLoader, preLoadLanguage} from './transloco.loader';

export const isLocalStorageAvailable = typeof localStorage !== 'undefined';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),

    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
      withComponentInputBinding()
    ),

    provideHttpClient(
      withInterceptors([
        retryOnNetworkErrorInterceptor, // retries 0/502/503/504 first
        unauthorizedInterceptor, // then handles 401
        countryInterceptor,
        commandMetadataInterceptor,
      ]),
      withFetch() // Fetch API backend instead of XMLHttpRequest
    ),
    provideOptimus({
      theme: {
        preset: optimusPreset,
        options: {
          darkModeSelector: '.dark',
          cssLayer: {
            name: 'optimus',
            order: 'theme, base, optimus',
          },
        },
      },
    }),

    provideGlobalExecution({sseEventFactory: CqrsEventFactory}),

    provideTransloco({
      config: {
        availableLangs: SUPPORTED_LANGUAGES.map(l => l.code),
        defaultLang: 'en',
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: CustomTranslocoLoader,
    }),

    isLocalStorageAvailable
      ? [
          provideTranslocoPersistLang({
            storageKey: 'frontend-sample_lang',
            storage: {useValue: localStorage},
          }),
          provideAppInitializer(preLoadLanguage),
        ]
      : [],
    importProvidersFrom(Angulartics2Module.forRoot({})),

    {provide: ENV_NAME, useValue: environment.envName},
    {provide: API_URL, useValue: environment.apiUrl},
    {provide: API_CONFIG, useValue: environment.apiConfig},
    {provide: PRODUCT_CONFIG_TOKEN, useClass: ProductConfigService},
    {provide: ACCESS_MANAGEMENT_CONFIG, useValue: sampleAccessManagementConfig},
    // CASL v7: `Ability` is the DI token (PureAbility was renamed; MongoAbility is
    // an interface and cannot serve as a token). The provided value is a Mongo ability.
    {provide: Ability, useValue: createSampleAppAbility()},
    {
      provide: LOGGER_CONFIG,
      useFactory: () => ({
        url: environment.apiConfig?.logger?.transferLog?.url,
        envName: environment.envName,
        appName: `${inject(PRODUCT_CONFIG_TOKEN).domain}/frontend`,
      }),
    },
    {provide: ErrorHandler, useClass: GlobalErrorHandler},

    // Initialize ability updater on app start
    provideAppInitializer(() => {
      // Service constructor will start watching for user changes
      inject(RootStore);
      inject(SseStore);
      inject(AuthStore);
    }),
  ],
};
